///-----------------------------------------------------------------------------
/// TODO: I want this to be more automated, but I failed to be able to figure it
/// out, even with claude helping me. I would love it if all of this code was
/// fully autogenerated based on some definition we made somewhere.
///-----------------------------------------------------------------------------
import merge from "lodash/merge";
import { Socket } from "socket.io-client";
import { Ping, Pong } from "../velocity/aurora/echo";
import {
    DataReq,
    DataRsp,
    PointDataReq,
    PointDataRsp,
    SchemaReq,
    SchemaRsp,
} from "../velocity/aurora/map";
import { SpatialDataRsp, SpatialReq } from "../velocity/aurora/spatial";

// Generic socket message handling infrastructure
type MessageHandler<T> = (message: T) => void;

interface SocketClient {
    socket: Socket;
}

const createSocketClient = (socket: Socket): SocketClient => {
    const client: SocketClient = {
        socket,
    };
    return client;
};

const subscribe = <T>(
    client: SocketClient,
    eventName: string,
    handler: MessageHandler<T>
) => {
    // Set up socket listener when first handler is added
    client.socket.on(eventName, handler);
    return () => {
        client.socket.off(eventName);
    };
};

const emit = <T>(client: SocketClient, eventName: string, message: T) => {
    if (!client.socket) {
        throw new Error("Socket not connected");
    }
    client.socket.emit(eventName, message);
};

const disconnect = (client: SocketClient) => {
    client.socket.disconnect();
};

export type AuroraCallBacks = {
    onPong: (handler: (p: Pong) => void) => () => void;
    sendPing: (ping: Ping) => void;

    // SCHEMA
    sendSchemaQuery: (req: SchemaReq) => void;
    onSchemaQueryResult: (handler: (p: SchemaRsp) => void) => void;

    // DATA
    sendDataQuery: (req: DataReq) => void;
    onDataQueryResult: (handler: (p: DataRsp) => void) => void;

    // POINT DATA
    sendPointDataQuery: (req: PointDataReq) => void;
    onPointDataQueryResult: (handler: (p: PointDataRsp) => void) => void;

    sendSpatialDataQuery: (req: SpatialReq) => void;
    onSpatialDataQueryResult: (handler: (p: SpatialDataRsp) => void) => void;

    disconnect: (callBack?: () => void) => void;
};
// Echo-specific implementation
export type AuroraClient = Socket & {
    serverUrl: string;
    dataSourceIds: readonly string[];
    callBacks: AuroraCallBacks;
};

export const getAuroraCallBacks = (socketClient: SocketClient) => {
    return {
        onPong: (handler: (p: Pong) => void) =>
            subscribe(socketClient, "pong", (message: ArrayBuffer) => {
                handler(Pong.decode(new Uint8Array(message)));
            }),

        sendPing: (ping: Ping) =>
            emit(socketClient, "ping", Ping.encode(ping).finish()),

        // SCHEMA
        sendSchemaQuery: (req: SchemaReq) => {
            emit(socketClient, "schema-req", SchemaReq.encode(req).finish());
        },

        onSchemaQueryResult: (handler: (p: SchemaRsp) => void) =>
            subscribe(socketClient, "schema-rsp", (message: ArrayBuffer) => {
                handler(SchemaRsp.decode(new Uint8Array(message)));
            }),

        // DATA
        sendDataQuery: (req: DataReq) => {
            emit(socketClient, "data-req", DataReq.encode(req).finish());
        },

        onDataQueryResult: (handler: (p: DataRsp) => void) =>
            subscribe(socketClient, "data-rsp", (message: ArrayBuffer) => {
                handler(DataRsp.decode(new Uint8Array(message)));
            }),

        // POINT DATA
        sendPointDataQuery: (req: PointDataReq) => {
            emit(
                socketClient,
                "point-data-req",
                PointDataReq.encode(req).finish()
            );
        },
        onPointDataQueryResult: (handler: (p: PointDataRsp) => void) =>
            subscribe(
                socketClient,
                "point-data-rsp",
                (message: ArrayBuffer) => {
                    handler(PointDataRsp.decode(new Uint8Array(message)));
                }
            ),

        sendSpatialDataQuery: (req: SpatialReq) => {
            emit(
                socketClient,
                "spatial-data-req",
                SpatialReq.encode(req).finish()
            );
        },
        onSpatialDataQueryResult: (handler: (p: SpatialDataRsp) => void) =>
            subscribe(
                socketClient,
                "spatial-data-rsp",
                (message: ArrayBuffer) => {
                    handler(SpatialDataRsp.decode(new Uint8Array(message)));
                }
            ),

        disconnect: (callBack?: () => void) => {
            disconnect(socketClient);
            callBack?.();
        },
    };
};

export const createAuroraClient = (
    socket: Socket,
    serverUrl: string,
    dataSourceIds: readonly string[]
): AuroraClient => {
    const socketClient = createSocketClient(socket);

    return merge(socketClient.socket, {
        serverUrl: serverUrl,
        dataSourceIds,
        callBacks: getAuroraCallBacks(socketClient),
    });
};
